import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import ErrorBoundary from './ErrorBoundary';
import { useIsMobile } from '../hooks/useIsMobile';

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  isActive ? 'app-nav-link app-nav-link--active' : 'app-nav-link';

function PersonIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM3.5 15a4.5 4.5 0 0 1 9 0H3.5Z" />
    </svg>
  );
}

function getPageLabel(pathname: string, workspaceId: string): string {
  if (!workspaceId || pathname === '/') return 'Workspaces';
  const base = `/workspaces/${workspaceId}`;
  if (pathname.startsWith(`${base}/sprints`)) return 'Sprints';
  if (pathname.startsWith(`${base}/teams`)) return 'Teams';
  if (pathname.startsWith(`${base}/labels`)) return 'Labels';
  if (pathname.startsWith(`${base}/projects`)) return 'Project';
  if (pathname.startsWith(`${base}/epics`)) return 'Epic';
  if (pathname === base) return 'Epics';
  return 'Quests';
}

const DROPDOWN_STYLE: React.CSSProperties = {
  position: 'absolute',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  boxShadow: '0 4px 16px rgba(16,24,40,0.12)',
  zIndex: 300,
  overflow: 'hidden',
};

export default function AppLayout() {
  const { workspaceId = '' } = useParams<{ workspaceId: string }>();
  const { user, logout } = useAuth();
  const location = useLocation();
  const isMobile = useIsMobile();

  const [navOpen, setNavOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onOutside(e: MouseEvent) {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setNavOpen(false);
      if (userRef.current && !userRef.current.contains(e.target as Node)) setUserMenuOpen(false);
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  const links = [
    { to: '/', label: 'Workspaces', end: true },
    { to: `/workspaces/${workspaceId}`, label: 'Epics', end: true },
    { to: `/workspaces/${workspaceId}/sprints`, label: 'Sprints', end: false },
    { to: `/workspaces/${workspaceId}/teams`, label: 'Teams', end: false },
    { to: `/workspaces/${workspaceId}/labels`, label: 'Labels', end: false },
  ];

  const pageLabel = getPageLabel(location.pathname, workspaceId);

  const dropdownItemStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    padding: '10px 16px',
    fontSize: 13,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    color: 'var(--color-text)',
  };

  return (
    <div>
      <nav className="app-nav" style={{ flexWrap: 'nowrap' }}>

        {/* Left: hamburger on mobile, full link row on desktop */}
        {isMobile ? (
          <div ref={navRef} style={{ position: 'relative', flexShrink: 1, minWidth: 0 }}>
            <button
              type="button"
              onClick={() => setNavOpen(v => !v)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                background: 'none',
                border: '1px solid var(--color-border)',
                borderRadius: 6,
                padding: '5px 10px',
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{ fontWeight: 700 }}>Quests</span>
              <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>·</span>
              <span>{pageLabel}</span>
              <span style={{ fontSize: 9, color: 'var(--color-text-muted)' }}>{navOpen ? '▲' : '▼'}</span>
            </button>
            {navOpen && (
              <div style={{ ...DROPDOWN_STYLE, top: 'calc(100% + 6px)', left: 0, minWidth: 170 }}>
                {links.map(l => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.end}
                    onClick={() => setNavOpen(false)}
                    style={({ isActive }) => ({
                      display: 'block',
                      padding: '10px 16px',
                      fontSize: 14,
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? 'var(--color-primary)' : 'var(--color-text)',
                      background: isActive ? 'rgba(37,99,235,0.06)' : 'none',
                      textDecoration: 'none',
                      borderLeft: isActive ? '3px solid var(--color-primary)' : '3px solid transparent',
                    })}
                  >
                    {l.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="app-nav-links">
            <span className="app-nav-brand">Quests</span>
            {links.map(l => (
              <NavLink key={l.to} to={l.to} end={l.end} className={navLinkClass}>
                {l.label}
              </NavLink>
            ))}
          </div>
        )}

        {/* Right: person icon + name → sign-out dropdown (desktop and mobile) */}
        <div ref={userRef} style={{ position: 'relative', flexShrink: 0 }}>
          <button
            type="button"
            onClick={() => setUserMenuOpen(v => !v)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: 'none',
              border: '1px solid var(--color-border)',
              borderRadius: 6,
              padding: '5px 10px',
              fontSize: 13,
              cursor: 'pointer',
              color: 'var(--color-text)',
              whiteSpace: 'nowrap',
            }}
          >
            <PersonIcon />
            <span style={{ maxWidth: isMobile ? 72 : 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.displayName}
            </span>
            <span style={{ fontSize: 9, color: 'var(--color-text-muted)' }}>{userMenuOpen ? '▲' : '▼'}</span>
          </button>
          {userMenuOpen && (
            <div style={{ ...DROPDOWN_STYLE, top: 'calc(100% + 6px)', right: 0, minWidth: 130 }}>
              <button
                type="button"
                onClick={() => { logout(); setUserMenuOpen(false); }}
                style={dropdownItemStyle}
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </nav>

      <ErrorBoundary resetKey={location.pathname}>
        <Outlet />
      </ErrorBoundary>
    </div>
  );
}
